import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Download, LogOut, FolderOpen, FileText, CreditCard, User, Layers, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const TABS = [
  { id: "projects", label: "Projects", icon: Layers },
  { id: "requests", label: "Requests", icon: Send },
  { id: "subscription", label: "Subscription", icon: CreditCard },
  { id: "files", label: "Files", icon: FolderOpen },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "account", label: "Account", icon: User },
];

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user) api.get("/portal/overview").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  if (user === null) return <div className="pt-40 pb-32 text-center text-slate-500" data-testid="portal-loading">Loading your portal…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  const download = async (fileId, filename) => {
    try {
      const r = await api.get(`/portal/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(fmtErr(e, "Download failed"));
    }
  };

  const activeProjects = data?.projects?.filter((p) => p.status !== "COMPLETED") || [];
  const doneProjects = data?.projects?.filter((p) => p.status === "COMPLETED") || [];

  return (
    <div className="pt-24 sm:pt-28 pb-24" data-testid="portal-page">
      <Seo title="Client Portal" path="/portal" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] text-arsenal">CLIENT PORTAL</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tight">
              {data?.user?.name ? `Welcome back, ${data.user.name.split(" ")[0]}.` : "Your studio dashboard."}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/start-a-project" data-testid="portal-new-request" className="clip-btn bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-5 py-2.5 transition-colors">
              NEW REQUEST
            </Link>
            <button
              data-testid="portal-logout"
              onClick={async () => { await logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-arsenal text-slate-300 font-mono text-xs tracking-[0.15em] px-4 py-2.5 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> LOG OUT
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-1 overflow-x-auto border-b border-white/10" role="tablist" aria-label="Portal sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`portal-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              role="tab"
              aria-selected={tab === t.id}
              className={`flex shrink-0 items-center gap-2 px-5 py-3 font-mono text-[11px] tracking-[0.15em] border-b-2 transition-colors ${
                tab === t.id ? "border-arsenal text-white" : "border-transparent text-slate-500 hover:text-white"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {!data && <p className="text-slate-500">Loading…</p>}

          {data && tab === "projects" && (
            <div className="space-y-10" data-testid="portal-projects">
              {[
                ["ACTIVE", activeProjects],
                ["COMPLETED", doneProjects],
              ].map(([label, list]) => (
                <div key={label}>
                  <p className="font-mono text-[11px] tracking-[0.25em] text-slate-500 mb-4">{label}</p>
                  {list.length === 0 && <p className="text-sm text-slate-600">Nothing here yet.</p>}
                  <div className="grid md:grid-cols-2 gap-5">
                    {list.map((p) => (
                      <Link
                        key={p.id}
                        to={`/portal/projects/${p.id}`}
                        data-testid={`portal-project-${p.id}`}
                        className="group block border border-white/10 bg-ink-surface hover:border-arsenal/50 p-6 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className={`clip-tag px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] ${p.status === "COMPLETED" ? "bg-white/10 text-slate-300" : "bg-arsenal text-white"}`}>{p.status}</span>
                          <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-arsenal transition-colors" />
                        </div>
                        <h3 className="mt-4 font-display text-lg font-bold text-white">{p.title}</h3>
                        <p className="mt-1 text-sm text-slate-500">{p.service} — {p.assigned_artist}</p>
                        <p className="mt-3 text-sm text-slate-400 line-clamp-2">{p.latest_update}</p>
                        {p.deadline && <p className="mt-3 font-mono text-[11px] tracking-[0.15em] text-slate-500">DEADLINE {p.deadline}</p>}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {data && tab === "requests" && (
            <div data-testid="portal-requests">
              {data.requests.length === 0 && <p className="text-sm text-slate-600">No briefs submitted yet.</p>}
              <div className="space-y-3">
                {data.requests.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-ink-surface px-5 py-4" data-testid={`portal-request-${r.id}`}>
                    <div>
                      <p className="font-semibold text-white">{r.title}</p>
                      <p className="text-sm text-slate-500 capitalize">{r.type.replace(/-/g, " ")} — {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="clip-tag bg-white/10 px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-slate-300">{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data && tab === "subscription" && (
            <div data-testid="portal-subscription">
              {data.subscription ? (
                <div className="max-w-lg border border-arsenal/40 bg-gradient-to-b from-arsenal/10 to-transparent p-8">
                  <span className="clip-tag bg-arsenal px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-white">{data.subscription.status}</span>
                  <h3 className="mt-4 font-display text-2xl font-black tracking-wider">{data.subscription.plan_name}</h3>
                  <div className="mt-4 space-y-2 text-sm text-slate-400">
                    <p>Active since {data.subscription.since}</p>
                    <p>Renews {data.subscription.renews}</p>
                    {data.subscription.note && <p className="text-slate-500">{data.subscription.note}</p>}
                  </div>
                </div>
              ) : (
                <div className="max-w-lg border border-white/10 bg-ink-surface p-8">
                  <h3 className="font-display text-xl font-bold">No active subscription</h3>
                  <p className="mt-2 text-sm text-slate-400">Monthly creative support, priority turnaround and included revisions.</p>
                  <Link to="/subscriptions" data-testid="portal-see-plans" className="clip-btn mt-6 inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors">
                    SEE PLANS <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </div>
          )}

          {data && tab === "files" && <FilesTab projects={data.projects} download={download} />}

          {data && tab === "invoices" && (
            <div className="border border-white/10" data-testid="portal-invoices">
              {data.invoices.length === 0 && <p className="p-6 text-sm text-slate-600">No invoices yet.</p>}
              {data.invoices.map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-white/5 last:border-0" data-testid={`invoice-${inv.number}`}>
                  <div>
                    <p className="font-mono text-sm text-white">{inv.number}</p>
                    <p className="text-sm text-slate-500">{inv.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-200">{inv.amount != null ? `$${inv.amount}` : inv.amount_label}</p>
                    <p className={`font-mono text-[10px] tracking-[0.15em] ${inv.status === "PAID" ? "text-emerald-400" : "text-arsenal"}`}>{inv.status}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {data && tab === "account" && <AccountTab user={data.user} />}
        </div>
      </div>
    </div>
  );
}

function FilesTab({ projects, download }) {
  const [files, setFiles] = useState(null);
  useEffect(() => {
    const withFiles = projects.filter((p) => p.status === "COMPLETED" || p.status === "APPROVED");
    Promise.all(withFiles.map((p) => api.get(`/portal/projects/${p.id}`).then((r) => r.data.deliverables.map((d) => ({ ...d, project: p.title }))).catch(() => [])))
      .then((r) => setFiles(r.flat()));
  }, [projects]);

  if (!files) return <p className="text-slate-500">Loading files…</p>;
  if (files.length === 0) return <p className="text-sm text-slate-600" data-testid="files-empty">No deliverables yet — final files appear here when projects complete.</p>;
  return (
    <div className="space-y-3" data-testid="portal-files">
      {files.map((f) => (
        <div key={f.id} className="flex items-center justify-between gap-3 border border-white/10 bg-ink-surface px-5 py-4">
          <div>
            <p className="font-mono text-xs tracking-[0.15em] text-arsenal">{f.label}</p>
            <p className="text-sm text-slate-400">{f.filename} — {f.project}</p>
          </div>
          <button data-testid={`file-download-${f.id}`} onClick={() => download(f.id, f.filename)} className="inline-flex items-center gap-2 border border-white/15 hover:border-arsenal text-slate-200 font-mono text-[11px] tracking-[0.15em] px-4 py-2 transition-colors">
            <Download className="h-3.5 w-3.5" /> DOWNLOAD
          </button>
        </div>
      ))}
    </div>
  );
}

function AccountTab({ user }) {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: user.name || "", discord: user.discord || "", company: user.company || "" });
  const [saved, setSaved] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await api.patch("/portal/account", form);
      setUser(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <form onSubmit={save} className="max-w-lg space-y-5" data-testid="portal-account">
      <p className="text-sm text-slate-500">Signed in as <span className="text-slate-300">{user.email}</span></p>
      {[["name", "Name"], ["discord", "Discord"], ["company", "Company"]].map(([k, label]) => (
        <label key={k} className="block">
          <span className="mb-1.5 block font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">{label}</span>
          <input data-testid={`account-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="aa-input" />
        </label>
      ))}
      <button type="submit" data-testid="account-save" className="clip-btn bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors">
        {saved ? "SAVED" : "SAVE CHANGES"}
      </button>
    </form>
  );
}
